/**
 * The customer's saved screens (DESIGN §9.2).
 *
 * A card per design, with a scaled `PhonePreview` as its thumbnail — the same
 * component the editor and the phone renderer agree with, so what the card
 * shows is what installs.
 */

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../api/client";
import { Empty, ErrorBox, Loading, relativeTime } from "../components/common";
import PhonePreview, { FRAME_WIDTH } from "../design/PhonePreview";
import { sampleBlocks, sampleRows } from "../design/modules";
import { useI18n } from "../i18n";

/** The card is a 170 px-wide phone; the frame itself is 360 dp. */
const THUMB_SCALE = 170 / FRAME_WIDTH;

export default function ScreenDesigns() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState(false);
  const [name, setName] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);

  const designs = useQuery({ queryKey: ["screen-designs"], queryFn: api.screenDesigns });
  // The same catalogue the editor previews against, so a thumbnail names a
  // module row "Last position" rather than `location.last_fix` (MODULES §2.7).
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });
  const samples = useMemo(() => sampleRows(modules.data ?? [], lang), [modules.data, lang]);
  const blockSamples = useMemo(
    () => sampleBlocks(modules.data ?? [], lang),
    [modules.data, lang],
  );
  // The list payload deliberately carries no asset URLs (it stays small), but
  // a thumbnail is the editor's preview, and an image block or an image
  // background resolves through the detail's presigned URLs — so fetch each
  // design's detail under the editor's cache key and hand the card the map.
  const details = useQueries({
    queries: (designs.data ?? []).map((design) => ({
      queryKey: ["screen-design", design.id],
      queryFn: () => api.screenDesign(design.id),
    })),
  });
  const assetUrls = useMemo(() => {
    const byDesign: Record<string, Record<string, string>> = {};
    (designs.data ?? []).forEach((design, i) => {
      const assets = details[i]?.data?.assets ?? [];
      byDesign[design.id] = Object.fromEntries(assets.map((asset) => [asset.id, asset.url]));
    });
    return byDesign;
    // `details` is positionally aligned with `designs.data`.
  }, [designs.data, details]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["screen-designs"] });

  const create = useMutation({
    mutationFn: () => api.createScreenDesign({ name: name.trim() }),
    onSuccess: (design) => {
      setDrawer(false);
      setName("");
      refresh();
      navigate(`/design/${design.id}`);
    },
  });
  const setDefault = useMutation({ mutationFn: api.setDefaultScreenDesign, onSuccess: refresh });
  const duplicate = useMutation({ mutationFn: api.duplicateScreenDesign, onSuccess: refresh });
  const remove = useMutation({
    mutationFn: api.deleteScreenDesign,
    onSuccess: () => {
      setConfirming(null);
      refresh();
    },
  });

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h2>{t("design.title")}</h2>
          <p className="page-sub">{t("design.intro")}</p>
        </div>
        <button className="primary" type="button" onClick={() => setDrawer(true)}>
          {t("design.new")}
        </button>
      </header>

      {designs.isLoading && <Loading what={t("design.title")} />}
      {designs.error && <ErrorBox error={designs.error} />}
      {setDefault.error && <ErrorBox error={setDefault.error} />}
      {duplicate.error && <ErrorBox error={duplicate.error} />}
      {remove.error && <ErrorBox error={remove.error} />}

      {designs.data?.length === 0 && (
        <section className="card">
          <Empty>{t("design.empty")}</Empty>
          <p className="muted small">{t("design.emptyHint")}</p>
        </section>
      )}

      {!!designs.data?.length && (
        <div className="grid cols-3 design-cards">
          {designs.data.map((design) => (
            <article className="card design-card" key={design.id}>
              <div className="design-card-thumb">
                <PhonePreview
                  spec={design.spec}
                  scale={THUMB_SCALE}
                  sampleRows={samples}
                  sampleBlocks={blockSamples}
                  assets={assetUrls[design.id] ?? {}}
                />
              </div>
              <div className="design-card-body">
                <h3>
                  {design.name}
                  {design.is_default && (
                    <span className="expiry-chip" style={{ marginLeft: 8 }}>
                      {t("design.default")}
                    </span>
                  )}
                </h3>
                <p className="muted small">
                  {t("design.updated", { when: relativeTime(design.updated_at, t) })}
                </p>
                <div className="row-actions">
                  <button type="button" onClick={() => navigate(`/design/${design.id}`)}>
                    {t("design.open")}
                  </button>
                  {!design.is_default && (
                    <button type="button" onClick={() => setDefault.mutate(design.id)}>
                      {t("design.setDefault")}
                    </button>
                  )}
                  <button type="button" onClick={() => duplicate.mutate(design.id)}>
                    {t("design.duplicate")}
                  </button>
                  {confirming === design.id ? (
                    <>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => remove.mutate(design.id)}
                      >
                        {t("design.confirmDelete")}
                      </button>
                      <button type="button" onClick={() => setConfirming(null)}>
                        {t("design.cancel")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setConfirming(design.id)}
                    >
                      {t("design.delete")}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {drawer && (
        <>
          <div className="drawer-backdrop" onClick={() => setDrawer(false)} />
          <aside className="drawer">
            <h3>{t("design.new")}</h3>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <label>
                {t("design.name")}
                <input
                  value={name}
                  autoFocus
                  maxLength={128}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </label>
              {create.error && <ErrorBox error={create.error} />}
              <div className="row">
                <button className="primary" type="submit" disabled={create.isPending}>
                  {create.isPending ? t("design.creating") : t("design.create")}
                </button>
                <button type="button" onClick={() => setDrawer(false)}>
                  {t("design.cancel")}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
    </div>
  );
}
